import { formatFullName } from '#core';
import { Dtos } from '#models';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  forwardRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { PrimeTemplate } from 'primeng/api';
import { TranslatePipe } from '@ngx-translate/core';
import { AutoComplete, AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { catchError, distinctUntilChanged, of } from 'rxjs';
import { UserSearchService } from 'src/services/user-search.service';

type UserSearchControlModel =
  | Pick<Dtos.User, 'Email' | 'ID' | 'FirstName' | 'LastName' | 'MiddleName'>
  | Pick<Dtos.SharedUser, 'Email' | 'UserID' | 'FullName' | 'ParentUserId'>
  | null;

@Component({
  selector: 'app-user-search-control',
  imports: [AutoComplete, PrimeTemplate, TranslatePipe, ReactiveFormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UserSearchControl),
      multi: true,
    },
  ],
  templateUrl: './user-search-control.html',
  styleUrl: './user-search-control.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSearchControl implements ControlValueAccessor {
  private _userSearchService = inject(UserSearchService);
  private _destroyRef = inject(DestroyRef);

  autoComplete = viewChild<AutoComplete>('autoComplete');
  suggestions = signal<(UserSearchControlModel | string)[]>([]);

  label = input<string>();
  placeholder = input<string>();
  isDisabled = input(false);
  isReadOnly = input(false);
  defaultValue = input<UserSearchControlModel | null>(null);

  formGroup = new FormGroup({
    selectedUser: new FormControl<UserSearchControlModel>(null),
  });

  invalid = input(false);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onTouched: () => void = () => {};

  formatValue = input<(val: UserSearchControlModel | string) => UserSearchControlModel | string>(
    (val: UserSearchControlModel | string) => {
      if (typeof val === 'string') {
        return val;
      }
      return val;
    }
  );

  searchUserByEmail({ query = '' }: AutoCompleteCompleteEvent) {
    this._userSearchService
      .searchUsers(query)
      .pipe(
        catchError(() => of([])),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(data => {
        const formattedResult = data.map(user => this.formatValue()(user));
        this.suggestions.set(formattedResult);
      });
  }

  writeValue(obj: UserSearchControlModel): void {
    this.formGroup.get('selectedUser')?.patchValue(obj, { emitEvent: false });

    this.formGroup.updateValueAndValidity({ emitEvent: false });
  }

  registerOnChange(fn: (value: UserSearchControlModel | null) => void): void {
    this.formGroup
      .get('selectedUser')
      ?.valueChanges.pipe(
        // debounceTime(1_000),
        distinctUntilChanged(),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(value => {
        fn(value || this.defaultValue());
      });
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    const ctrl = this.formGroup.get('selectedUser');
    if (isDisabled) {
      ctrl?.disable({ emitEvent: false });
    } else {
      ctrl?.enable({ emitEvent: false });
    }
  }

  onBlur() {
    // Get the value from the form control or from the autocomplete input directly
    let currentValue: UserSearchControlModel | string | null | undefined = this.formGroup.get('selectedUser')?.value;
    let inputTextValue = '';

    // If value is null/undefined or doesn't have Email, try to get it from the autocomplete input element
    if (!currentValue || (typeof currentValue === 'object' && !currentValue.Email)) {
      const autoCompleteInstance = this.autoComplete();
      if (autoCompleteInstance?.el?.nativeElement) {
        const inputElement = autoCompleteInstance.el.nativeElement.querySelector('input') as HTMLInputElement;
        if (inputElement?.value) {
          inputTextValue = inputElement.value;
          currentValue = inputTextValue;
        }
      }
    }

    // If the current value is a string (pasted/typed text)
    if (typeof currentValue === 'string') {
      const textValue: string = currentValue;
      if (textValue.trim()) {
        // Keep the text as string value regardless if it's an email or name
        // Only convert to User object when explicitly selected from suggestions list
        // This allows validation to know there's text but prevents enabling save button
        this.formGroup
          .get('selectedUser')
          ?.setValue(textValue.trim() as unknown as UserSearchControlModel, { emitEvent: true });
      }
    }

    // Mark as touched after processing the value
    this.formGroup.get('selectedUser')?.markAsTouched();
    this._onTouched();
  }

  onClear() {
    this.formGroup.get('selectedUser')?.setValue(this.defaultValue());
  }

  getOptionLabel(user: Dtos.User): string {
    const fullName = formatFullName(user);
    // If no name is available, show the email instead
    return fullName && fullName.trim() ? fullName : user.Email || '';
  }

  // Distinguishes a real name from the email fallback so the suggestion template
  // doesn't render the email twice.
  hasDisplayName(user: Dtos.User): boolean {
    return !!formatFullName(user)?.trim();
  }
}
